import { FormikInput, FormikSelectA11y, FormikTime } from '@glow/formik-components'
import { ButtonBase, IconButton } from '@glow/ui-components'
import { formikValidationRequired } from '@glow/validators'
import { Form, Formik, FormikValues } from 'formik'
import i18next from 'i18next'
import { Map } from 'immutable'
import { Duration } from 'luxon'
import { BreakType } from '../../actions/creators/helpers'
import { workingTimeHours } from '../../utils/breakTimeUtils'
import { validBreakTimeWindow } from '../unit/breakInputValidation'
import { Break } from '../unit/UnitForm'

function validateTimeWindow(values: FormikValues) {
  return values.earliest && values.latest && values.duration
    ? validBreakTimeWindow(values.earliest, values.latest, values.duration)
      ? undefined
      : i18next.t('error.notAValidTimeWindow')
    : undefined
}

type Props = {
  updatedBreak?: Break
  courierBreak: Map<string, string>
  editBreakValues: (values: FormikValues) => void
  setEditMode: (value: boolean) => void
  breakDuration: number
  updatedBreakDuration?: number | string
}

export const BreakForm = ({
  updatedBreak,
  courierBreak,
  editBreakValues,
  setEditMode,
  breakDuration,
  updatedBreakDuration
}: Props) => {
  const maxWorkingTime = courierBreak.get('maxWorkingTime')
    ? Duration.fromISO(courierBreak.get('maxWorkingTime') || '').hours
    : ''
  const updatedMaxWorkingTime =
    updatedBreak &&
    'maxWorkingTime' in updatedBreak &&
    updatedBreak.maxWorkingTime &&
    Duration.fromISO(updatedBreak?.maxWorkingTime || '').hours

  return (
    <Formik
      initialValues={{
        earliest: updatedBreak && 'earliest' in updatedBreak ? updatedBreak?.earliest : courierBreak.get('earliest'),
        latest: updatedBreak && 'latest' in updatedBreak ? updatedBreak?.latest : courierBreak.get('latest'),
        duration: updatedBreakDuration ?? breakDuration,
        maxWorkingTime: updatedMaxWorkingTime ?? maxWorkingTime
      }}
      onSubmit={(values) => {
        editBreakValues(values)
      }}
    >
      {({ values }) => (
        <Form aria-label={i18next.t('courier.editBreakTime')} className="flex justify-between p-4">
          <div className="flex gap-4">
            {courierBreak.get('type') === BreakType.WorkingTime ? (
              <FormikSelectA11y
                style={{ zIndex: 1000 }}
                name={'maxWorkingTime'}
                showNoneOption={false}
                options={workingTimeHours()}
                label={i18next.t('courier.maxWorkingTime')}
              />
            ) : (
              <div className="flex gap-2">
                <FormikTime
                  label={i18next.t('courier.timeWindow')}
                  name="earliest"
                  validate={(value) =>
                    formikValidationRequired(value, i18next.t('error.requiredEarliestStartBreak')) ||
                    validateTimeWindow(values)
                  }
                  className="w-44!"
                />
                <span className="mt-7">–</span>
                <div>
                  <FormikTime
                    name="latest"
                    label={i18next.t('courier.latestBreakStart')}
                    hideLabel={true}
                    className="w-44! mt-[1.1rem]"
                    validate={(value) => formikValidationRequired(value, i18next.t('error.requiredLatestStartBreak'))}
                  />
                </div>
              </div>
            )}
            <FormikInput
              name={'duration'}
              label={i18next.t('courier.breakDuration')}
              validate={(value: string) => formikValidationRequired(value, i18next.t('error.requiredBreakDuration'))}
            />
            <ButtonBase variant={'secondary'} type="submit" className="mt-[1.1rem]">
              {i18next.t('courier.updateBreakTime')}
            </ButtonBase>
          </div>
          <div>
            <IconButton
              icon="xmark"
              size="md"
              aria-label={i18next.t('button.cancel')}
              onClick={() => setEditMode(false)}
            />
          </div>
        </Form>
      )}
    </Formik>
  )
}
